// frontend/src/LiveTableLogsByBranch.jsx  (FINAL - CANONICAL /table-log ROUTER COMPATIBLE)
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { API, apiJSON } from "./apiBase";

function fmtTime(v) {
  if (!v) return "";
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
    try {
      return new Date(s).toLocaleString();
    } catch {
      return s;
    }
  }
  return s;
}

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function contactToText(c) {
  if (!c) return "";
  if (Array.isArray(c)) return c.filter(Boolean).join(" • ");
  if (typeof c === "object") {
    const name = (c.name ?? "").toString().trim();
    const phones = (c.phones ?? "").toString().trim();
    const birthday = (c.birthday ?? "").toString().trim();
    return [name, phones, birthday].filter(Boolean).join(" • ");
  }
  return String(c);
}

// ✅ Raw fetch helper (cookie-auth safe) + real error messages
async function fetchJSON(path) {
  const base = String(API || "").replace(/\/$/, "");
  const url = `${base}${path.startsWith("/") ? path : `/${path}`}`;

  const res = await fetch(url, {
    method: "GET",
    cache: "no-store",
    credentials: "include",
    headers: { Accept: "application/json" },
  });

  const text = await res.text().catch(() => "");
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    // keep data null
  }

  if (!res.ok) {
    const msg = data?.detail || data?.error || text || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.body = text;
    throw err;
  }

  return data;
}

export default function LiveTableLogsByBranch() {
  const [branches, setBranches] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [branchErrors, setBranchErrors] = useState({}); // {branch: "401 Unauthorized ..."}

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    setBranchErrors({});

    try {
      // 1) Allowed branches for this user (canonical)
      // Response: { branches: [...], is_admin, current_branch }
      const br = await apiJSON("/table-log/branches", { method: "GET" });
      const branchList = Array.isArray(br?.branches) ? br.branches : [];
      const normalized = branchList
        .map((b) => String(b || "").trim().toLowerCase())
        .filter(Boolean);

      setBranches(normalized);

      // 2) Load logs per branch (canonical)
      // GET /table-log?branch=<b>&limit=...&max_kb=...
      const allLogs = [];
      const perBranchErr = {};

      for (const b of normalized) {
        try {
          const data = await fetchJSON(
            `/table-log?branch=${encodeURIComponent(b)}&limit=200&max_kb=1024`
          );

          const logs = Array.isArray(data?.logs) ? data.logs : [];
          const resolved = String(data?.branch || b || "").toLowerCase();

          for (const it of logs) {
            allLogs.push({
              ...it,
              branch: resolved,
            });
          }
        } catch (e) {
          perBranchErr[b] = `❌ ${b}: ${e?.status || ""} ${e?.message || "Failed"}`;
        }
      }

      setBranchErrors(perBranchErr);

      // 3) newest first (ISO compare if ISO exists)
      allLogs.sort((a, b) =>
        String(b?.time || "").localeCompare(String(a?.time || ""))
      );
      setItems(allLogs);
    } catch (e) {
      console.error(e);
      setErr(`Failed to load logs: ${e?.message || String(e)}`);
      setItems([]);
      setBranches([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const out = {};
    for (const b of branches) out[String(b).toLowerCase()] = { tables: 0, guests: 0 };

    for (const it of items) {
      const b = String(it?.branch || "").toLowerCase();
      if (!out[b]) out[b] = { tables: 0, guests: 0 };
      out[b].tables += 1;
      out[b].guests += safeArray(it?.contacts).length;
    }
    return out;
  }, [items, branches]);

  const totals = useMemo(() => {
    let tables = 0;
    let guests = 0;
    Object.values(stats).forEach((v) => {
      tables += v.tables || 0;
      guests += v.guests || 0;
    });
    return { tables, guests };
  }, [stats]);

  return (
    <div style={{ padding: 12 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <button className="btn primary small" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>

        <div style={{ opacity: 0.85 }}>
          Total: <b>{totals.tables}</b> tables • <b>{totals.guests}</b> guests
        </div>
      </div>

      {err && <div style={{ marginTop: 10, color: "#ef4444" }}>{err}</div>}

      {/* Per-branch errors (do NOT hide!) */}
      {Object.keys(branchErrors).length > 0 && (
        <div
          style={{
            marginTop: 10,
            padding: 10,
            border: "1px solid rgba(239,68,68,.35)",
            borderRadius: 10,
          }}
        >
          <div style={{ fontWeight: 800, marginBottom: 6, color: "#ef4444" }}>
            Branch load errors
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            {Object.values(branchErrors).map((t, i) => (
              <div key={i} style={{ fontSize: 13, opacity: 0.95 }}>
                {t}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{ marginTop: 15 }}>
        <h3 style={{ margin: 0 }}>Live Stats (All Branches)</h3>
        <div style={{ marginTop: 8, display: "grid", gap: 8 }}>
          {branches.map((bRaw) => {
            const b = String(bRaw).toLowerCase();
            const s = stats[b] || { tables: 0, guests: 0 };
            return (
              <div
                key={b}
                style={{
                  padding: 10,
                  border: "1px solid rgba(148,163,184,0.25)",
                  borderRadius: 10,
                }}
              >
                <b style={{ textTransform: "capitalize" }}>{b}</b> — Tables:{" "}
                <b>{s.tables}</b> • Guests: <b>{s.guests}</b>
              </div>
            );
          })}
          {branches.length === 0 && !loading && (
            <div style={{ opacity: 0.75 }}>
              No branches returned. (If you’re not admin, check user.branches or permissions.)
            </div>
          )}
        </div>
      </div>

      {/* Logs */}
      <div style={{ marginTop: 18, display: "grid", gap: 10 }}>
        {items.map((it, idx) => (
          <div
            key={`${it.branch || "b"}-${it.time || idx}-${idx}`}
            style={{
              padding: 12,
              border: "1px solid rgba(148,163,184,0.25)",
              borderRadius: 12,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
              <div style={{ fontWeight: 800 }}>
                🧾 Table {it.table || "-"} • {String(it.branch || "").toUpperCase()}
              </div>
              <div style={{ fontSize: 12, opacity: 0.75 }}>{fmtTime(it.time)}</div>
            </div>

            <div style={{ marginTop: 6, display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div>
                👩 Hostess: <b>{it.hostess || "-"}</b>
              </div>
              <div>
                🧑 Waiter: <b>{it.waiter || "-"}</b>
              </div>
              <div>
                👥 Guests: <b>{safeArray(it.contacts).length}</b>
              </div>
            </div>

            <div style={{ marginTop: 10 }}>
              <div style={{ fontWeight: 700 }}>Contacts</div>
              <div style={{ marginTop: 6, display: "grid", gap: 6 }}>
                {safeArray(it.contacts).slice(0, 30).map((c, i) => (
                  <div
                    key={`${idx}-${i}`}
                    style={{
                      padding: "6px 8px",
                      border: "1px solid rgba(148,163,184,0.18)",
                      borderRadius: 10,
                      fontSize: 13,
                      wordBreak: "break-word",
                    }}
                  >
                    {contactToText(c) || "-"}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}

        {items.length === 0 && !loading && <div style={{ opacity: 0.75 }}>No logs yet.</div>}
      </div>
    </div>
  );
}